// dataLayer events for the Pineapple Event Schema (GTM-PW6LWPGC).
// Event and property names are the contract with the GTM workspace; GA4
// cannot rename them retrospectively, so change them only with the
// tracking plan.
import TagManager from 'react-gtm-module';
import type { Address } from '@pineapple/address-search';
import { Network, Plan } from '../models/plans';
import { Router } from '../models/routers';
import { SignUP } from '../features/signup/signupSlice';
import { Coupon } from '../features/coupon/couponAPI';
import {
  Discount,
  discountFromCoupon,
} from '../features/discount/discountSlice';
import {
  getDiscountPlanCost,
  GetMonthlyCost,
} from '../components/OrderSummary';

const STATIC_IP_PRICE = 9;
const NEW_DEVELOPMENT_FEE = 300;

const push = (dataLayer: Record<string, unknown>) => {
  TagManager.dataLayer({ dataLayer, dataLayerName: 'dataLayer' });
};

// Form pages are offset by one from the tracked steps: page 1 is the
// address check, which is not a step.
const stepNames = [
  'BUILD_YOUR_PLAN',
  'CONTACT_INFO',
  'CONNECTION_DETAILS',
  'INSTALLATION',
  'PAYMENT',
  'CONFIRM_ORDER',
];

const serviceableStatuses = ['LIT', 'ComingSoon', 'BuildCommenced'];

export const isServiceable = (status?: string) => (
  serviceableStatuses.includes(status ?? '')
);

type AddressLike = Partial<Pick<
  Address, 'status' | 'suburb' | 'postcode' | 'uniqueCode' | 'buildingCategory'
>>;

const networkLabel: Record<Network, string> = {
  dgtek: 'DGTEK',
  communityfibre: 'COMMUNITY_FIBRE',
};

// The network the address is served on. With a settled qualification the
// network is given (null = none); otherwise it is read off the DGtek
// verdict the address carries, as before Community Fibre.
export const networkType = (addr: AddressLike, network?: Network | null) => {
  if (network !== undefined) return network === null ? 'NONE' : networkLabel[network];
  return isServiceable(addr.status) || addr.buildingCategory === 'greenfield'
    ? 'DGTEK' : 'NONE';
};

// The network an order is on: the chosen plan says so for Community Fibre.
const orderNetworkType = (signUp: SignUP, addr: AddressLike) => (
  signUp.plan?.network ? networkLabel[signUp.plan.network] : networkType(addr)
);

// download/upload as advertised; DGtek plans are symmetric.
const planSpeed = (plan: Plan) => `${plan.download ?? plan.speed}/${plan.upload ?? plan.speed}`;

export const routerOption = (router: Router) => {
  if (router.mode === 'No Router Required' || router.cost === '0') {
    return 'BYO';
  }
  if (router.mode === 'Basic Wi-Fi Router PAYG') return 'MONTHLY';
  return 'OUTRIGHT';
};

export const trackAddressCheckSubmit = (widgetLocation: string) => {
  push({ event: 'address_check_submit', widget_location: widgetLocation });
};

export const trackAddressCheckResult = (addr: AddressLike, network?: Network | null) => {
  // The website hands a failed check over with uniqueCode=undefined
  const uniqueCode = addr.uniqueCode && addr.uniqueCode !== 'undefined'
    ? addr.uniqueCode : null;
  push({
    event: 'address_check_result',
    serviceable: network !== undefined ? network !== null : isServiceable(addr.status),
    network_type: networkType(addr, network),
    suburb: addr.suburb,
    postcode: addr.postcode,
    unique_code: uniqueCode,
  });
};

export const trackAddressRegister = (addr: AddressLike) => {
  push({
    event: 'address_register',
    suburb: addr.suburb,
    postcode: addr.postcode,
  });
};

export const trackPlanSelect = (plan: Plan) => {
  push({
    event: 'plan_select',
    plan_name: plan.name,
    plan_speed: planSpeed(plan),
  });
};

export const trackStaticIpToggle = (enabled: boolean, plan: Plan) => {
  push({
    event: 'static_ip_toggle',
    static_ip_enabled: enabled,
    plan_name: plan.name,
  });
};

export const trackRouterSelect = (router: Router, plan: Plan) => {
  push({
    event: 'router_select',
    router_name: router.name,
    router_option: routerOption(router),
    plan_name: plan.name,
  });
};

const staticIpEnabled = (signUp: SignUP) => signUp.addOns.includes('static-ip');

// Item prices are what is charged on activation, so they add up to the
// "Total charge on activation" shown to the customer.
const ecommercePayload = (
  signUp: SignUP,
  addr: AddressLike,
  discount?: Discount,
) => {
  const { plan, router } = signUp;
  const items: Array<Record<string, unknown>> = [
    {
      // The back end plan id, so items reconcile with Chargebee
      item_id: plan.id,
      item_name: plan.name,
      item_category: 'plan',
      item_variant: planSpeed(plan),
      price: getDiscountPlanCost(GetMonthlyCost(plan), discount),
      quantity: 1,
    },
    {
      item_id: `router_${routerOption(router).toLowerCase()}`,
      item_name: router.name,
      item_category: 'router',
      item_variant: routerOption(router),
      price: parseFloat(router.cost) || 0,
      quantity: 1,
    },
  ];
  if (staticIpEnabled(signUp)) {
    items.push({
      item_id: 'addon_static_ip',
      item_name: 'Static IP',
      item_category: 'addon',
      price: STATIC_IP_PRICE,
      quantity: 1,
    });
  }
  if (addr.buildingCategory === 'greenfield') {
    items.push({
      item_id: 'fee_new_development',
      item_name: 'New Development Charge',
      item_category: 'fee',
      price: NEW_DEVELOPMENT_FEE,
      quantity: 1,
    });
  }

  return {
    currency: 'AUD',
    value: items.reduce((acc, i) => acc + (i.price as number), 0),
    items,
  };
};

export const trackAddToCart = (
  signUp: SignUP,
  addr: AddressLike,
  discount?: Discount,
) => {
  // Clear the previous ecommerce payload so its items do not leak
  push({ ecommerce: null });
  push({
    event: 'add_to_cart',
    static_ip_enabled: staticIpEnabled(signUp),
    network_type: orderNetworkType(signUp, addr),
    ecommerce: ecommercePayload(signUp, addr, discount),
  });
};

let lastTransactionId = '';

export const trackPurchase = (
  transactionId: string,
  signUp: SignUP,
  addr: AddressLike,
  discount?: Discount,
) => {
  // GA4 deduplicates on transaction_id, never send the same order twice
  if (transactionId === lastTransactionId) return;
  lastTransactionId = transactionId;

  push({ ecommerce: null });
  push({
    event: 'purchase',
    network_type: orderNetworkType(signUp, addr),
    static_ip_enabled: staticIpEnabled(signUp),
    suburb: addr.suburb,
    postcode: addr.postcode,
    ecommerce: {
      transaction_id: transactionId,
      ...(signUp.coupon?.coupon_id
        ? { coupon: signUp.coupon.coupon_id.toUpperCase() } : {}),
      ...ecommercePayload(signUp, addr, discount),
    },
  });
};

export const trackStepView = (page: number) => {
  const step = page - 1;
  if (step < 1 || step > stepNames.length) return;
  push({
    event: 'signup_step_view',
    step_number: step,
    step_name: stepNames[step - 1],
  });
};

// Step 1 is excluded, its forward action is add_to_cart.
export const trackStepComplete = (page: number) => {
  const step = page - 1;
  if (step < 2 || step > stepNames.length) return;
  push({
    event: 'signup_step_complete',
    step_number: step,
    step_name: stepNames[step - 1],
  });
};

// Pass the coupon the back end returned when the code was accepted.
export const trackPromoCodeValidate = (
  code: string,
  plan: Plan,
  coupon?: Coupon,
) => {
  let discountValue = 0;
  if (coupon) {
    const discount = discountFromCoupon(coupon);
    const planCost = GetMonthlyCost(plan);
    if (discount.dollar_amount) {
      discountValue = discount.dollar_amount / 100;
    } else if (discount.percentage) {
      discountValue = (planCost * parseFloat(discount.percentage)) / 100;
    }
  }
  push({
    event: 'promo_code_validate',
    coupon: (code ?? '').trim().toUpperCase(),
    promo_valid: coupon !== undefined,
    discount_value: discountValue,
  });
};
